import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Minus, Search, X } from 'lucide-react'
import DatePicker from '../DatePicker/DatePicker'
import './FilterSideModal.scss'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isFilterActive = (filter, value) => {
  if (value === undefined || value === null || value === '') return false
  if (filter.type === 'checked-list') return Array.isArray(value) && value.length > 0
  if (filter.type === 'chip' && (value === '' || value === filter.allValue)) return false
  if (filter.type === 'number-range') return value?.from !== '' || value?.to !== ''
  if (filter.type === 'date-range') return Boolean(value?.start || value?.end)
  return true
}

const countActiveFilters = (filters, values) =>
  filters.filter((f) => isFilterActive(f, values[f.key])).length

const shouldRenderFilter = (filter, values) => {
  if (!filter?.showWhen) return true

  const condition = filter.showWhen
  const watchedValue = values?.[condition.key]

  if (Array.isArray(condition.in)) {
    return condition.in.includes(watchedValue)
  }

  if (Object.prototype.hasOwnProperty.call(condition, 'equals')) {
    return watchedValue === condition.equals
  }

  if (Object.prototype.hasOwnProperty.call(condition, 'notEquals')) {
    return watchedValue !== condition.notEquals
  }

  return true
}

// ─── Chip / pill toggle (single-select with optional "All") ───────────────────

function ChipField({ filter, value, onChange }) {
  const labelKey = filter.labelKey || 'label'
  const valueKey = filter.valueKey || 'value'
  const allValue = filter.allValue ?? ''
  const current = value ?? allValue

  return (
    <div className="fsm-chip-group">
      <button
        type="button"
        className={`fsm-chip ${current === allValue ? 'fsm-chip--active' : ''}`}
        onClick={() => onChange(filter.key, allValue)}
      >
        {filter.allLabel || 'All'}
      </button>
      {(filter.options || []).map((opt) => (
        <button
          key={opt[valueKey]}
          type="button"
          className={`fsm-chip ${current === opt[valueKey] ? 'fsm-chip--active' : ''}`}
          onClick={() => onChange(filter.key, opt[valueKey])}
        >
          {opt[labelKey]}
        </button>
      ))}
    </div>
  )
}

// ─── Number range ─────────────────────────────────────────────────────────────

function NumberRangeField({ filter, value, onChange }) {
  const from = value?.from ?? ''
  const to = value?.to ?? ''
  const fromLabel = filter.fromLabel || 'From'
  const toLabel = filter.toLabel || 'To'
  const fromPlaceholder = filter.fromPlaceholder ?? '0'
  const toPlaceholder = filter.toPlaceholder ?? '100,000'

  return (
    <div className="fsm-range">
      <div className="fsm-range__col">
        <span className="fsm-range__sub-label">{fromLabel}</span>
        <input
          type="number"
          className="fsm-input"
          placeholder={fromPlaceholder}
          value={from}
          onChange={(e) => onChange(filter.key, { from: e.target.value, to })}
        />
      </div>
      <span className="fsm-range__sep">-</span>
      <div className="fsm-range__col">
        <span className="fsm-range__sub-label">{toLabel}</span>
        <input
          type="number"
          className="fsm-input"
          placeholder={toPlaceholder}
          value={to}
          onChange={(e) => onChange(filter.key, { from, to: e.target.value })}
        />
      </div>
    </div>
  )
}

// ─── Date range ───────────────────────────────────────────────────────────────
function DateRangeField({ filter, value, onChange }) {
  const start = value?.start || ''
  const end = value?.end || ''
  const fromLabel = filter.fromLabel || 'From'
  const toLabel = filter.toLabel || 'To'
  const formatDateValue = (nextValue) => (nextValue && typeof nextValue.format === 'function' ? nextValue.format('YYYY-MM-DD') : nextValue)

  return (
    <div className="fsm-range">
      <div className="fsm-range__col">
        <span className="fsm-range__sub-label">{fromLabel}</span>
        <DatePicker
          value={start}
          // maxDate={end || undefined}
          onChange={(nextValue) => {onChange(filter.key, { start: formatDateValue(nextValue), end }); console.log("check", formatDateValue(nextValue))}}
        />
      </div>
      <span className="fsm-range__sep">-</span>
      <div className="fsm-range__col">
        <span className="fsm-range__sub-label">{toLabel}</span>
        <DatePicker
          value={end}
          minDate={start || undefined}
          onChange={(nextValue) => onChange(filter.key, { start, end: formatDateValue(nextValue) })}
        />
      </div>
    </div>
  )
}

// ─── Text ─────────────────────────────────────────────────────────────────────

function TextField({ filter, value, onChange }) {
  return (
    <>
      <span className="fsm-range__sub-label">{filter.inputLabel || filter.label}</span>
      <input
        type="text"
        className="fsm-input"
        placeholder={filter.placeholder || ''}
        value={value || ''}
        onChange={(e) => onChange(filter.key, e.target.value)}
      />
    </>
  )
}

// ─── Select ───────────────────────────────────────────────────────────────────

function SelectField({ filter, value, onChange }) {
  const labelKey = filter.labelKey || 'label'
  const valueKey = filter.valueKey || 'value'

  return (
    <>
      <span className="fsm-range__sub-label">{filter.inputLabel || filter.label}</span>
      <div className="fsm-select-wrap">
        <select
          className="fsm-select"
          value={value || ''}
          onChange={(e) => onChange(filter.key, e.target.value)}
        >
          <option value="">{filter.placeholder || 'All'}</option>
          {(filter.options || []).map((opt) => (
            <option key={opt[valueKey]} value={opt[valueKey]}>
              {opt[labelKey]}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}

// ─── Checked list (outlet selector) ──────────────────────────────────────────

function CheckedListField({ filter, value, onChange }) {
  const labelKey = filter.labelKey || 'label'
  const valueKey = filter.valueKey || 'value'
  const [search, setSearch] = useState('')

  const selectedValues = Array.isArray(value)
    ? value
    : value === '' || value === null || value === undefined
      ? []
      : [value]

  const options = Array.isArray(filter.options) ? filter.options : []
  const filteredOptions = options.filter((opt) =>
    String(opt?.[labelKey] || '')
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  )

  const allValues = filteredOptions.map((opt) => opt?.[valueKey]).filter((v) => v !== undefined)
  const selectedInViewCount = allValues.filter((v) => selectedValues.includes(v)).length
  const isAllSelected = allValues.length > 0 && selectedInViewCount === allValues.length
  const isPartialSelected = selectedInViewCount > 0 && selectedInViewCount < allValues.length

  const toggleValue = (nextValue) => {
    const hasValue = selectedValues.includes(nextValue)
    const next = hasValue
      ? selectedValues.filter((v) => v !== nextValue)
      : [...selectedValues, nextValue]
    onChange(filter.key, next)
  }

  const toggleSelectAll = () => {
    if (!allValues.length) return

    if (isAllSelected) {
      onChange(filter.key, selectedValues.filter((v) => !allValues.includes(v)))
      return
    }

    const merged = [...new Set([...selectedValues, ...allValues])]
    onChange(filter.key, merged)
  }

  return (
    <div className="fsm-checked-list">
      <div className="fsm-checked-list__search-wrap">
        <Search size={18} className="fsm-checked-list__search-icon" />
        <input
          type="text"
          className="fsm-checked-list__search-input"
          placeholder={filter.placeholder || 'Search'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!!search && (
          <button
            type="button"
            className="fsm-checked-list__clear-btn"
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="fsm-checked-list__body">
        <button
          type="button"
          className="fsm-checked-list__row fsm-checked-list__row--select-all"
          onClick={toggleSelectAll}
        >
          <span
            className={`fsm-checked-list__checkbox ${isAllSelected || isPartialSelected ? 'fsm-checked-list__checkbox--active' : ''}`}
          >
            {isAllSelected ? <Check size={14} /> : isPartialSelected ? <Minus size={14} /> : null}
          </span>
          <span>Select all</span>
        </button>

        <div className="fsm-checked-list__options">
          {filteredOptions.map((opt) => {
            const optionValue = opt?.[valueKey]
            const isSelected = selectedValues.includes(optionValue)

            return (
              <button
                key={String(optionValue)}
                type="button"
                className="fsm-checked-list__row"
                onClick={() => toggleValue(optionValue)}
              >
                <span className={`fsm-checked-list__checkbox ${isSelected ? 'fsm-checked-list__checkbox--active' : ''}`}>
                  {isSelected ? <Check size={14} /> : null}
                </span>
                <span>{opt?.[labelKey]}</span>
              </button>
            )
          })}
          {!filteredOptions.length && (
            <div className="fsm-checked-list__empty">No options found.</div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Field dispatcher ─────────────────────────────────────────────────────────

function FilterField({ filter, value, onChange }) {
  switch (filter.type) {
    case 'checked-list':
      return <CheckedListField filter={filter} value={value} onChange={onChange} />
    case 'chip':
      return <ChipField filter={filter} value={value} onChange={onChange} />
    case 'number-range':
      return <NumberRangeField filter={filter} value={value} onChange={onChange} />
    case 'date-range':
      return <DateRangeField filter={filter} value={value} onChange={onChange} />
    case 'select':
      return <SelectField filter={filter} value={value} onChange={onChange} />
    case 'text':
    default:
      return <TextField filter={filter} value={value} onChange={onChange} />
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * FilterSideModal
 *
 * Props:
 *   open        – boolean
 *   onClose     – () => void
 *   onApply     – (values: object) => void
 *   onReset     – () => void
 *   title       – string  (default: "Filters")
 *   values      – object  { [key]: value }
 *   onChange    – (key, value) => void
 *   filters     – FilterDef[]
 *
 * FilterDef:
 *   key            – string
 *   label          – string
 *   type           – 'chip' | 'number-range' | 'date-range' | 'select' | 'text'
 *
 *   chip:
 *     options      – { label, value }[]
 *     allLabel     – string   (default 'All')
 *     allValue     – any      (default '')
 *     labelKey / valueKey
 *
 *   number-range:
 *     fromLabel    – string   (default 'From')
 *     toLabel      – string   (default 'To')
 *     fromPlaceholder / toPlaceholder
 *
 *   date-range:
 *     fromLabel / toLabel
 *
 *   select:
 *     options, placeholder, labelKey, valueKey
 *
 *   conditional rendering:
 *     showWhen: {
 *       key: string,
 *       equals?: any,
 *       notEquals?: any,
 *       in?: any[]
 *     }
 */
export default function FilterSideModal({
  open,
  onClose,
  onApply,
  onReset,
  title = 'Filters',
  values = {},
  onChange,
  filters = [],
}) {
  if (!open) return null

  const visibleFilters = filters.filter((filter) => shouldRenderFilter(filter, values))
  const activeCount = countActiveFilters(visibleFilters, values)

  return createPortal(
    <>
      <div className="fsm-overlay" onClick={onClose} aria-hidden="true" />

      <aside className="fsm-panel" role="dialog" aria-modal="true" aria-label={title}>
        {/* Header */}
        <div className="fsm-panel__header">
          <h3 className="fsm-panel__title">
            {title}{activeCount > 0 && <span className="fsm-panel__count"> ({activeCount})</span>}
          </h3>
          <button className="fsm-icon-btn" type="button" onClick={onClose} aria-label="Close filters">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="fsm-panel__body">
          {visibleFilters.map((filter, index) => (
            <div key={filter.key}>
              {index > 0 && <div className="fsm-divider" />}
              <div className="fsm-field">
                {!['text', 'select'].includes(filter.type) && (
                  <span className="fsm-field__label">{filter.label}</span>
                )}
                <FilterField
                  filter={filter}
                  value={values[filter.key]}
                  onChange={onChange}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="fsm-panel__footer">
          <button type="button" className="fsm-btn fsm-btn--outline" onClick={onReset}>
            Clear all
          </button>
          <button type="button" className="fsm-btn fsm-btn--primary" onClick={() => onApply?.(values)}>
            Apply filters
          </button>
        </div>
      </aside>
    </>,
    document.body,
  )
}
